import React from 'react';

import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { FullCycleRenderWithProfiler } from '@confluence/ssr-utilities/entry-points/FullCycleRender.ts';
import { NativeCollabPresenceStateContainer } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { useIsLivePageWithSSR } from '@confluence/live-pages-utils/entry-points/useIsLivePageWithSSR.ts';
import { VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

import { TreeRootForServerPlaceholderWrapper } from '../TreeRootForServerPlaceholderWrapper.tsx';
import { FullPageEditorLoader } from '../FullPageEditorLoader.ts';

type EditPageLoaderServerProps = {
	contentId: string;
	spaceKey: string;
	draftShareId?: string;
	contentType: string;
};

// This component is supposed to be server-side version of EditPageLoader and will be used
// for rendering content area / breadcrumbs and possibly editor toolbar, i.e. anything being
// rendered by FullPageEditorLoader. At the current stage we skip server-rendering of content
// area so the component is empty.
export const EditPageLoaderServer = ({
	contentId,
	spaceKey,
	draftShareId,
	contentType,
}: EditPageLoaderServerProps): React.JSX.Element => {
	const livePageSSRStatus = useIsLivePageWithSSR();
	if (livePageSSRStatus.isTypeKnown && livePageSSRStatus.isLive) {
		// Remove draftShareId if it exists, to avoid persisting it on live doc. If the param exists on live doc, it prevents SSR from preloading live doc title/content
		return (
			<Redirection
				name={VIEW_PAGE.name}
				params={{ contentId, spaceKey }}
				excludedQueryParams={['draftShareId']}
			/>
		);
	}

	return isSSRStreaming() ? (
		<SuspenseWithDataFetcher dataFetcherName="content-main">
			<TreeRootForServerPlaceholderWrapper>
				<NativeCollabPresenceStateContainer scope={contentId}>
					<FullPageEditorLoader
						// Matching the logic in NativeCollabComponent
						// confluence/next/packages/native-collab/src/NativeCollabComponent.tsx#getDraftMeta
						isUnpublishedDraft={!!draftShareId}
						contentId={contentId}
						spaceKey={spaceKey}
						draftShareId={draftShareId}
						contentType={contentType}
						collabEditState={{}}
						heartbeatData={{}}
						disabled
					/>
				</NativeCollabPresenceStateContainer>
			</TreeRootForServerPlaceholderWrapper>
		</SuspenseWithDataFetcher>
	) : (
		<FullCycleRenderWithProfiler name="edit-page">
			<TreeRootForServerPlaceholderWrapper>
				<NativeCollabPresenceStateContainer scope={contentId}>
					<FullPageEditorLoader
						// Matching the logic in NativeCollabComponent
						// confluence/next/packages/native-collab/src/NativeCollabComponent.tsx#getDraftMeta
						isUnpublishedDraft={!!draftShareId}
						contentId={contentId}
						spaceKey={spaceKey}
						draftShareId={draftShareId}
						contentType={contentType}
						collabEditState={{}}
						heartbeatData={{}}
						disabled
					/>
				</NativeCollabPresenceStateContainer>
			</TreeRootForServerPlaceholderWrapper>
		</FullCycleRenderWithProfiler>
	);
};
